import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Tenant } from "api/generatedApi";
import { SelectInput, SliderInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";

import { FieldColumns } from "../../components/FieldColumns";
import { SubjectFormSchema } from "../forms/subjectFormSchema";

type CustomFieldsProps = {
  customFields: Tenant["custom_fields"];
  control: Control<SubjectFormSchema>;
  disabled?: boolean;
};

export function CustomFields({
  customFields,
  control,
  disabled,
}: CustomFieldsProps) {
  const { t, i18n } = useTranslation();

  if (!customFields?.length) {
    return null;
  }

  const getLocalizedText = (data: { [key: string]: string }): string => {
    return data?.[i18n.language] || data?.["en"] || "";
  };

  return (
    <SectionCard title={t("Additional information")}>
      <FieldColumns>
        {customFields.map((field) => {
          const fieldName = `custom_fields.${field.id}` as const;
          const title = getLocalizedText(field.title);

          if (field.type === "dropdown") {
            const options = Object.values(field.options || {}).map(
              (option) => ({
                id: option.value,
                label: getLocalizedText(option.name),
              }),
            );

            return (
              <SelectInput
                key={field.id}
                control={control}
                fieldName={fieldName}
                label={title}
                options={options}
                placeholder={getLocalizedText(field.placeholder || {})}
                disabled={disabled}
              />
            );
          }

          if (field.type === "textinput") {
            return (
              <TextInput
                key={field.id}
                control={control}
                fieldName={fieldName}
                label={title}
                placeholder={getLocalizedText(field.placeholder || {})}
                variant="outlined"
                disabled={disabled}
              />
            );
          }

          if (field.type === "slider") {
            return (
              <SliderInput
                key={field.id}
                control={control}
                fieldName={fieldName}
                label={title}
                min={field.min}
                max={field.max}
                disabled={disabled}
              />
            );
          }

          return null;
        })}
      </FieldColumns>
    </SectionCard>
  );
}
